import React, { useState, useEffect, useRef, useMemo,useContext } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  InputAdornment,
  IconButton,
  Alert,
  LinearProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Divider,
} from "@mui/material";
import { Visibility, VisibilityOff, Business, LocationOn, Description, AccountCircle, CheckCircle, Upload, Lock, Close, ArrowBack, ArrowForward } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import api from "../../api/axios";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../../utils/SnackbarProvider";
import { usePhoneOTP } from "../../hooks/usePhoneOTP";
import {AuthContext} from "../../context/AuthContext";

import MapPicker from "./MapPicker";

// Step definitions
const STEPS = [
  { label: 'Account', icon: <AccountCircle /> },
  { label: 'Business', icon: <Business /> },
  { label: 'Details', icon: <Description /> },
  { label: 'Address', icon: <LocationOn /> },
];

// Password Strength Function
const getPasswordStrength = (password) => {
  let score = 0;
  if (!password) return 0;
  if (password.length >= 6) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score;
};

const strengthLabel = ["Very Weak", "Weak", "Moderate", "Strong", "Very Strong"];
const strengthColor = ["#D32F2F", "#F57C00", "#FBC02D", "#388E3C", "#2E7D32"];

// Required field indicator
const RequiredLabel = ({ children }) => (
  <span>{children} <span style={{ color: "#ff0000" }}>*</span></span>
);

// Reusable doc upload + preview block (image thumbnail or filename, with X delete)
const DocUpload = ({ label, accentColor = '#1976d2', file, preview, onFileChange, onDelete }) => {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [preview]);

  const isImagePreview = Boolean(preview) && !imageFailed && (
    preview.startsWith('blob:') ||
    preview.startsWith('data:') ||
    /\.(jpe?g|png|gif|webp|bmp|svg)(\?.*)?$/i.test(preview)
  );

  return (
    <Box>
      <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
        {label}
      </Typography>
      <Button variant="outlined" size="small" component="label"
        sx={{ borderColor: accentColor, color: accentColor }}>
        <Upload fontSize="small" sx={{ mr: 0.5 }} />
        {file ? "Change" : "Upload PDF / JPG"}
        <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={onFileChange} />
      </Button>
      {file && (
        <Box sx={{ mt: 1, position: 'relative', display: 'inline-block' }}>
          {isImagePreview ? (
            <Box component="img" src={preview} alt={label}
              sx={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 1,
                border: '1px solid #e0e0e0', cursor: 'pointer', display: 'block' }}
              onClick={() => window.open(preview, '_blank')}
              onError={() => setImageFailed(true)} />
          ) : (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.75,
                bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0', maxWidth: 180,
                cursor: preview ? 'pointer' : 'default' }}
              onClick={() => preview && window.open(preview, '_blank')}>
              <Description fontSize="small" sx={{ color: '#ff9900' }} />
              <Typography variant="caption" sx={{ color: '#555', overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {preview ? 'Open file' : file.name}
              </Typography>
            </Box>
          )}
          <IconButton size="small" onClick={onDelete}
            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff',
              border: '1px solid #e0e0e0', width: 20, height: 20,
              '&:hover': { bgcolor: '#ffebee' } }}>
            <Close sx={{ fontSize: 12, color: '#d32f2f' }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

// Modern button style (matching VendorRegistration)
const modernButtonStyle = {
  borderRadius: 2,
  textTransform: 'none',
  fontWeight: 600,
  px: 3,
  py: 1,
  transition: 'all 0.2s ease',
  boxShadow: '0 2px 8px rgba(255,153,0,0.2)',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(255,153,0,0.3)',
  },
};

// Combined agent schema with all fields (vendor pattern — single schema for reset() compatibility)
const agentSchema = z.object({
  // Step 0: Account
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Invalid email"),
  mobile: z.string().min(8, "Mobile number required"),
  password: z.string()
    .optional()
    .refine(
      (val) => !val || val.length >= 6,
      {
        message:
          "Password must be at least 6 characters",
      }
    ),
  confirmPassword: z.string().optional(),
  // Step 1: Business
  preferredLanguage: z.string().optional(),
  agentType: z.string().optional(),
  areaOfOperation: z.string().optional(),
  legalName: z.string().optional(),
  businessRegNumber: z.string().optional(),
  incorporationDate: z.string().optional(),
  // Step 2: Contact
  contactPerson: z.string().optional(),
  designation: z.string().optional(),
  contactPhone: z.string().optional(),
  contactEmail: z.string().optional().or(z.literal("")),
  altContact: z.string().optional(),
  // Step 3: Address (same fields as vendor)
  address: z.object({
    addressLine1: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    pincode: z.string().optional(),
    location: z.object({
      type: z.string().optional(),
      coordinates: z.array(z.number()).optional(),
    }).nullable().optional(),
}).optional(),
  businessAddress: z.string().optional(),
  website: z.string().optional(),
  // India compliance fields (conditional on areaOfOperation = India | Both)
  gstin: z.string().optional(),
  panNumber: z.string().optional(),
  iecCode: z.string().optional(),
  // China compliance fields (conditional on areaOfOperation = China | Both)
  usccCode: z.string().optional(),
  chinaBusinessLicenseNumber: z.string().optional(),
}).refine((data) => {
  if (!data.password) return true;
  return data.password === data.confirmPassword;
}, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

const AgentRegistration = () => {
  const navigate = useNavigate();
  const { user, agentProfile: profile,loading:loadingagent, logout, updateUser,updateAgentProfile } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Change Password Dialog
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirmNew, setShowConfirmNew] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, color: "#eee" });

  // Password strength handler
  const evaluatePasswordStrength = (pwd) => {
    let score = 0;
    if (!pwd) return { score: 0, color: "#eee" };
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    const colors = ["#D32F2F", "#F57C00", "#FBC02D", "#388E3C", "#2E7D32"];
    return { score, color: colors[score] || "#2E7D32" };
  };

  // Password change schema
  const PasswordSchema = z.object({
    currentPassword: z.string().min(6, "Minimum 6 characters"),
    newPassword: z.string().min(6, "Minimum 6 characters"),
    confirmNewPassword: z.string().min(6, "Minimum 6 characters"),
  }).refine(data => data.newPassword === data.confirmNewPassword, {
    message: "Passwords do not match",
    path: ["confirmNewPassword"],
  });

  // Separate form for password change dialog
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors },
  } = useForm({
    resolver: zodResolver(PasswordSchema),
    mode: "onBlur",
  });

  // Profile image upload
  const [profilePic, setProfilePic] = useState("");
  // const [profilePicFile, setProfilePicFile] = useState(null);
  const profileInputRef = useRef(null);

  // Business license upload
  const [businessLicense, setBusinessLicense] = useState(null);
  const [businessLicensePreview, setBusinessLicensePreview] = useState("");

  // India compliance document states
  const [gstCertFile, setGstCertFile] = useState(null);
  const [gstCertPreview, setGstCertPreview] = useState("");
  const [panCardFile, setPanCardFile] = useState(null);
  const [panCardPreview, setPanCardPreview] = useState("");
  const [iecCertFile, setIecCertFile] = useState(null);
  const [iecCertPreview, setIecCertPreview] = useState("");

  // China compliance document states
  const [usccCertFile, setUsccCertFile] = useState(null);
  const [usccCertPreview, setUsccCertPreview] = useState("");
  const [chinaLicenseFile, setChinaLicenseFile] = useState(null);
  const [chinaLicensePreview, setChinaLicensePreview] = useState("");

  // Tracks which compliance doc fields to clear in DB on save (user deleted existing doc)
  const [deletedDocs, setDeletedDocs] = useState([]);
  const [businessLicenseDeleted, setBusinessLicenseDeleted] = useState(false);

  // User email (for OTP in edit mode)
  const [userEmail, setUserEmail] = useState("");

  const {
    register,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(agentSchema),
    mode: "onBlur",
    defaultValues: {
      name: "",
      email: "",
      mobile: "",
      password: "",
      confirmPassword: "",
      preferredLanguage: "",
      agentType: "",
      areaOfOperation: "",
      legalName: "",
      businessRegNumber: "",
      incorporationDate: "",
      contactPerson: "",
      designation: "",
      contactPhone: "",
      contactEmail: "",
      altContact: "",
      address: { 
        addressLine1: "", 
        city: "", 
        state: "", 
        pincode: "", 
        location: null },
      businessAddress: "",
      website: "",
      gstin: "",
      panNumber: "",
      iecCode: "",
      usccCode: "",
      chinaBusinessLicenseNumber: "",
    },
  });

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    return parts.length > 1
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name[0].toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = ["#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7", "#DDA0DD", "#98D8C8", "#F7DC6F", "#BB8FCE", "#85C1E9"];
    let hash = 0;
    if (name) {
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const handleProfileUpload = async (e) => {
    const file = e.target.files?.[0];
      if (!file) return;
      setProfilePic(user.profilePic || ""); // Show existing profile pic until upload completes 
          const formData = new FormData();
          formData.append("image", file);
      
          const res = await api.post(
            "/users/profile-picture",
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
          setProfilePic(res.data.profilePic);
          // Update AuthContext
    updateUser(
      {
        ...user,
        profilePic: res.data.profilePic,
      },
      true
    );
    showSnackbar("Profile picture uploaded successfully", "success");
    // if (file) {
    //   setProfilePicFile(file);
    //   setProfilePic(URL.createObjectURL(file));
    // }
  };

  const originalMobileRef = useRef("");
  const currentMobile = watch("mobile");
  const password = watch("password", "");

  // ── usePhoneOTP hook ──
  const otp = usePhoneOTP();

  // ── Reset hook state when mobile number changes ──
  useEffect(() => {
    if (!originalMobileRef.current && currentMobile) {
      originalMobileRef.current = currentMobile;
      return;
    }
    if (
      originalMobileRef.current &&
      currentMobile !== originalMobileRef.current
    ) {
      otp.resetOTP();
      showSnackbar(
        "Mobile number changed. Please verify the new number.",
        "warning"
      );
    }
  }, [currentMobile, showSnackbar]); // eslint-disable-line react-hooks/exhaustive-deps

  // initialLocation for map picker — reactive to location changes (auto-geocode)
  const location = watch("address.location");
  const initialLocation = useMemo(() => {
    if (
      location?.coordinates &&
      location.coordinates.length === 2 &&
      location.coordinates[0] !== 0 &&
      location.coordinates[1] !== 0
    ) {
      return {
        latitude: location.coordinates[1],
        longitude: location.coordinates[0],
      };
    }
    return { lat: 0, lng: 0, address: "" };
  }, [location]);

  // Load existing profile (edit mode)
  const loadProfile = async () => {
    let userData = null;
    try {
    //   const [profileRes, docsRes] = await Promise.all([
    //   api.get("/agent/profile"),
    //   api.get("/agent/profile/documents"),
    // ]);

    // const profilePromise = api.get("/agent/profile"); 
const docsPromise = api.get("/agent/profile/documents");
const [ docsRes] = await Promise.allSettled([ docsPromise]);
    // const { user, profile } = profileRes.status === "fulfilled" ? profileRes.value.data : { user: null, profile: null };

// Safe defaults
const documents = docsRes.status === "fulfilled" ? docsRes.value.data?.documents || {} : {};
const indiaUrls = documents.complianceUrls?.india || {};
const chinaUrls = documents.complianceUrls?.china || {};

const localUser = JSON.parse(
  localStorage.getItem("user") || "{}"
);

const hasPhone = !!(user?.mobile && user.mobile.trim() !== "");
otp.setPhoneVerified(hasPhone);

if (profile) {

  const hasExistingProfile = !!(
    profile.legalName ||
    profile.businessRegNumber ||
    profile.contactPerson ||
    profile.address?.addressLine1 ||
    profile.address?.city
  );

  if (localUser.isRegistration && hasExistingProfile) {
    setNeedsPassword(true);
  }

  userData = user;
  setIsEditMode(true);
  setUserEmail((user?.email || "").toLowerCase());
  otp.setPhoneVerified(true);

  reset({
    name: profile?.name || "",
    email: (user?.email || "").toLowerCase(),
    mobile: user?.mobile || "",
    preferredLanguage: profile.preferredLanguage || "",
    agentType: profile.agentType || "",
    areaOfOperation: profile.areaOfOperation || "",
    legalName: profile.legalName || "",
    businessRegNumber: profile.businessRegNumber || "",
    incorporationDate: profile.incorporationDate
      ? new Date(profile.incorporationDate).toISOString().slice(0, 10)
      : "",
    contactPerson: profile.contactPerson || "",
    designation: profile.designation || "",
    contactPhone: profile.contactPhone || "",
    contactEmail: profile.contactEmail || "",
    altContact: profile.altContact || "",
    address: {
      addressLine1: profile.address?.addressLine1 || "",
      city: profile.address?.city || "",
      state: profile.address?.state || "",
      pincode: profile.address?.pincode || "",
      location: profile.address?.location || null,
    },
    businessAddress: profile.businessAddress || "",
    website: profile.website || "",
    gstin: profile.compliance?.india?.gstin || "",
    panNumber: profile.compliance?.india?.panNumber || "",
    iecCode: profile.compliance?.india?.iecCode || "",
    usccCode: profile.compliance?.china?.usccCode || "",
    chinaBusinessLicenseNumber:
      profile.compliance?.china?.chinaBusinessLicenseNumber || "",
  });

  originalMobileRef.current = user?.mobile || "";

  /* -------------------- PROFILE IMAGE -------------------- */
if(user.profilePic) {
  setProfilePic(user.profilePic);
}
  // if (documents.profileImageUrl) {
  //   setProfilePic(documents.profileImageUrl);
  // } else if (profile.profileImage?.startsWith("data:")) {
  //   setProfilePic(profile.profileImage);
  // }

  /* -------------------- BUSINESS LICENSE -------------------- */

  const businessLicenseUrl =
    documents.businessLicenseUrl || profile.businessLicense;

  if (businessLicenseUrl) {
    setBusinessLicense({
      name: "business_license",
      type: "image",
    });

    setBusinessLicensePreview(businessLicenseUrl);
  }

  /* -------------------- GST -------------------- */

  if (indiaUrls.gstCertificate) {
    setGstCertFile({ name: "gst_certificate" });
    setGstCertPreview(indiaUrls.gstCertificate);
  }

  /* -------------------- PAN -------------------- */

  if (indiaUrls.panCard) {
    setPanCardFile({ name: "pan_card" });
    setPanCardPreview(indiaUrls.panCard);
  }

  /* -------------------- IEC -------------------- */

  if (indiaUrls.iecCertificate) {
    setIecCertFile({ name: "iec_certificate" });
    setIecCertPreview(indiaUrls.iecCertificate);
  }

  /* -------------------- USCC -------------------- */

  if (chinaUrls.usccCertificate) {
    setUsccCertFile({ name: "uscc_certificate" });
    setUsccCertPreview(chinaUrls.usccCertificate);
  }

  /* -------------------- CHINA LICENSE -------------------- */

  if (chinaUrls.chinaBusinessLicense) {
    setChinaLicenseFile({
      name: "china_business_license",
    });

    setChinaLicensePreview(
      chinaUrls.chinaBusinessLicense
    );
  }

} else if (user) {

  userData = user;

  setIsEditMode(true);

  if (localUser.isRegistration) {
    setNeedsPassword(true);
  }

  setUserEmail((user.email || "").toLowerCase());

  reset({
    name: user.name || "",
    email: (user.email || "").toLowerCase(),
    mobile: user.mobile || "",
  });

  originalMobileRef.current = user.mobile || "";
}
    } catch (err) {
      // Not logged in or no profile yet — stay in registration mode
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
     if (loadingagent) return;
    loadProfile();
  }, [loadingagent, user, profile]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ============================
     AUTO GEOCODE ADDRESS
     ============================ */
  const geocodeAddress = async (address) => {
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

      const res = await axios.get(
        "https://maps.googleapis.com/maps/api/geocode/json",
        {
          params: {
            address,
            key: apiKey,
          },
        }
      );

      if (res.data.results.length > 0) {
        const loc = res.data.results[0].geometry.location;

        return {
          type: "Point",
          coordinates: [loc.lng, loc.lat],
        };
      }

      return null;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  useEffect(() => {
    const timer = setTimeout(async () => {
      const address = [
        watch("address.addressLine1"),
        watch("address.city"),
        watch("address.state"),
        watch("address.pincode"),
        "India",
      ]
        .filter(Boolean)
        .join(", ");

      if (address.length > 10) {
        const geo = await geocodeAddress(address);

        if (geo) {
          setValue("address.location", geo);
        }
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [
    watch("address.addressLine1"),
    watch("address.city"),
    watch("address.state"),
    watch("address.pincode"),
  ]);

  /* ── Thin wrapper: validate fields, then delegate to the hook ── */
  const handleSendOTP = async () => {
    const mobile = getValues("mobile");
    const email = getValues("email");
    const name = getValues("name");
    if (!mobile || !email || !name) {
      showSnackbar("Please fill name, email and mobile first", "warning");
      return;
    }
    const sent = await otp.handleSendOTP(mobile);
    if (sent) {
      showSnackbar("OTP sent to your WhatsApp", "success");
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp.otpCode || otp.otpCode.length < 4) {
      showSnackbar("Enter a valid 4-digit OTP", "warning");
      return;
    }
    const verified = await otp.handleVerifyOTP(getValues("mobile"), otp.otpCode);
    if (verified) {
      originalMobileRef.current = getValues("mobile");
      showSnackbar("Mobile verified!", "success");
    }
  };

  const handleStepClick = (step) => setActiveStep(step);

  const validateStep = (step) => {
    switch (step) {

      case 0:
        if (!watch("name")) {
          showSnackbar("Name is required", "error");
          return false;
        }

        if (!watch("email")) {
          showSnackbar("Email is required", "error");
          return false;
        }

        if (!watch("mobile")) {
          showSnackbar("Mobile number is required", "error");
          return false;
        }

        if (!otp.phoneVerified) {
          showSnackbar(
            "Please verify mobile number first",
            "error"
          );
          return false;
        }

        if (needsPassword || !isEditMode) {
          const pw = watch("password");
          const cpw = watch("confirmPassword");

          if (!pw || pw.length < 6) {
            showSnackbar(
              "Password must be at least 6 characters",
              "error"
            );
            return false;
          }

          if (pw !== cpw) {
            showSnackbar(
              "Passwords do not match",
              "error"
            );
            return false;
          }
        }

        return true;

      case 1:
        if (!watch("preferredLanguage")) {
          showSnackbar(
            "Preferred language is required",
            "error"
          );
          return false;
        }

        if (!watch("agentType")) {
          showSnackbar(
            "Agent type is required",
            "error"
          );
          return false;
        }

        if (!watch("areaOfOperation")) {
          showSnackbar(
            "Area of operation is required",
            "error"
          );
          return false;
        }

        {
          const area = watch("areaOfOperation");
          if (area === "India" || area === "Both") {
            if (!watch("gstin") && !watch("panNumber") && !watch("iecCode")) {
              showSnackbar("At least one India compliance ID required: GSTIN, PAN, or IEC", "error");
              return false;
            }
          }
          if (area === "China" || area === "Both") {
            if (!watch("usccCode")) {
              showSnackbar("USCC Code is required for China operations", "error");
              return false;
            }
          }
        }

        return true;

      case 2:
        if (!watch("contactPerson")) {
          showSnackbar(
            "Contact person is required",
            "error"
          );
          return false;
        }

        if (!watch("contactPhone")) {
          showSnackbar(
            "Contact phone is required",
            "error"
          );
          return false;
        }

        return true;

      case 3:
        if (!watch("address.addressLine1")) {
          showSnackbar(
            "Business address is required",
            "error"
          );
          return false;
        }

        if (!watch("address.city")) {
          showSnackbar(
            "City is required",
            "error"
          );
          return false;
        }

        if (!watch("address.state")) {
          showSnackbar(
            "State is required",
            "error"
          );
          return false;
        }

        if (!watch("address.pincode")) {
          showSnackbar(
            "Pincode is required",
            "error"
          );
          return false;
        }

        return true;

      default:
        return true;
    }
  };

  const handleNext = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (activeStep === 0) {

      if (!watch("name")) {
        showSnackbar("Name is required", "error");
        return;
      }

      if (!watch("email")) {
        showSnackbar("Email is required", "error");
        return;
      }

      if (!watch("mobile")) {
        showSnackbar("Mobile number is required", "error");
        return;
      }

      if (!otp.phoneVerified) {
        showSnackbar(
          "Please verify your mobile number first",
          "error"
        );
        return;
      }

      if (needsPassword || !isEditMode) {

        const pw = watch("password");
        const cpw = watch("confirmPassword");

        if (!pw || pw.length < 6) {
          showSnackbar(
            "Password must be at least 6 characters",
            "error"
          );
          return;
        }

        if (pw !== cpw) {
          showSnackbar(
            "Passwords do not match",
            "error"
          );
          return;
        }
      }
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  };

  const handlePasswordChange = async (data) => {
    try {
      await api.put("/auth/change-password", {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      showSnackbar("Password changed successfully!", "success");
      setOpenPasswordDialog(false);
      resetPasswordForm();
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to change password", "error");
    }
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    try {

      for (let step = 0; step < STEPS.length; step++) {
        if (!validateStep(step)) {
          setActiveStep(step);

          showSnackbar(
            "Please fix the errors on this step before saving",
            "error"
          );

          setLoading(false);   // ADD THIS

          return;
        }
      }
      // If new user, create login first
      if (!isEditMode) {
        const loginRes = await api.post("/auth/login", {
          identifier: getValues("mobile"),
          role: "agent",
          email: getValues("email"),
          name: getValues("name"),
          loginLocation: { latitude: null, longitude: null },
        });

        if (loginRes.data?.token) {
          localStorage.setItem(
            "token",
            loginRes.data.token
          );
        }

        if (loginRes.data?.user) {
          localStorage.setItem(
            "user",
            JSON.stringify(loginRes.data.user)
          );
        }
      }

      // Build FormData for agent profile
      const fd = new FormData();
      const profileFields = [
        "name", "legalName", "businessRegNumber", "incorporationDate",
        "agentType", "areaOfOperation", "preferredLanguage",
        "contactPerson", "designation", "contactPhone", "contactEmail",
        "altContact",
        "businessAddress", "website",
      ];
      profileFields.forEach((f) => {
        const v = getValues(f);
        if (v !== undefined && v !== null && v !== "") {
          fd.append(f, v);
        }
      });

      // Send location as GeoJSON Point or fall back to lat/lng
      const address = getValues("address");
      if (address) {
        const normalizedAddress = { ...address };
        if (address.location && typeof address.location !== "string") {
          normalizedAddress.location = JSON.stringify(address.location);
        }
        fd.append("address", JSON.stringify(normalizedAddress));
      }

      // if (profilePicFile) {
      //   fd.append("profileImage", profilePicFile);
      // }
      if (businessLicense instanceof File) {
        fd.append("businessLicense", businessLicense);
      }

      // Build compliance object (text fields)
      const areaOfOperation = getValues("areaOfOperation");
      const showIndia = areaOfOperation === "India" || areaOfOperation === "Both";
      const showChina = areaOfOperation === "China" || areaOfOperation === "Both";
      const complianceObj = {};
      if (showIndia) {
        complianceObj.india = {};
        const gstin = getValues("gstin"); if (gstin) complianceObj.india.gstin = gstin;
        const panNumber = getValues("panNumber"); if (panNumber) complianceObj.india.panNumber = panNumber;
        const iecCode = getValues("iecCode"); if (iecCode) complianceObj.india.iecCode = iecCode;
      }
      if (showChina) {
        complianceObj.china = {};
        const usccCode = getValues("usccCode"); if (usccCode) complianceObj.china.usccCode = usccCode;
        const cbn = getValues("chinaBusinessLicenseNumber"); if (cbn) complianceObj.china.chinaBusinessLicenseNumber = cbn;
      }
      if (Object.keys(complianceObj).length > 0) {
        fd.append("compliance", JSON.stringify(complianceObj));
      }

      // Compliance document uploads
      if (showIndia) {
        if (gstCertFile instanceof File) fd.append("gstCertificate", gstCertFile);
        if (panCardFile instanceof File) fd.append("panCard", panCardFile);
        if (iecCertFile instanceof File) fd.append("iecCertificate", iecCertFile);
      }
      if (showChina) {
        if (usccCertFile instanceof File) fd.append("usccCertificate", usccCertFile);
        if (chinaLicenseFile instanceof File) fd.append("chinaBusinessLicense", chinaLicenseFile);
      }

      // Tell backend which existing docs to clear
      if (deletedDocs.length > 0) {
        fd.append("deleteComplianceDocs", JSON.stringify(deletedDocs));
      }
      if (businessLicenseDeleted) {
        fd.append("deleteBusinessLicense", "true");
      }

      if (needsPassword) {

        const pw = getValues("password");
        const cpw = getValues("confirmPassword");

        if (!pw || pw.length < 6) {
          showSnackbar(
            "Password must be at least 6 characters",
            "error"
          );
          setLoading(false);
          return;
        }

        if (pw !== cpw) {
          showSnackbar(
            "Passwords do not match",
            "error"
          );
          setLoading(false);
          return;
        }

        const user = JSON.parse(
          localStorage.getItem("user") || "{}"
        );

        const username =
          user.username ||
          user.email ||
          getValues("email");

        const res = await api.post(
          "/auth/set-password",
          {
            username,
            password: pw,
          }
        );

        localStorage.setItem("token", res.data.token);
        localStorage.setItem(
          "user",
          JSON.stringify(res.data.user)
        );
      }
      
      const res = await api.put("/agent/profile", fd);
      showSnackbar(isEditMode ? "Profile updated successfully!" : "Agent registration completed!", "success");

      // Clear isRegistration flag so the header shows dashboard link
      // and fetches the profile image (both gated behind !isRegistration)
      //if (!isEditMode || needsPassword) {
        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        updateAgentProfile(res.data.profile,true);
        if (storedUser.isRegistration) {
          delete storedUser.isRegistration;
          localStorage.setItem("user", JSON.stringify(storedUser));
        }
      //}

      navigate("/agent/dashboard");
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to save profile", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBusinessLicense(file);
      setBusinessLicenseDeleted(false);
      if (file.type.startsWith("image/")) {
        setBusinessLicensePreview(URL.createObjectURL(file));
      } else {
        setBusinessLicensePreview("");
      }
    } else {
      setBusinessLicense(null);
      setBusinessLicensePreview("");
    }
  };

  const renderModernField = (label, field, required = false) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
        {label} {required && <span style={{ color: '#ff9900' }}>*</span>}
      </Typography>
      {field}
    </Box>
  );

  const renderModernTextField = (props) => (
    <TextField
      {...props}
      fullWidth
      variant="outlined"
      size="small"
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
          transition: 'all 0.2s ease',
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ff9900',
            },
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ff9900',
              borderWidth: 2,
            },
          },
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: '#ff9900',
        },
      }}
    />
  );

  // Show loading while checking profile
  if (initialLoading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: '#ff9900' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", pt: 2, pb: 4, px: 1, bgcolor: "#f8f9fa" }}>
      <Box sx={{ maxWidth: 900, mx: 'auto', mb: 2, px: 1 }}>
        <Paper sx={{ p: { xs: 1, sm: 2 }, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.875rem' } } }}>
            {STEPS.map((step, index) => (
              <Step key={step.label} sx={{ cursor: 'pointer' }} onClick={() => handleStepClick(index)}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      '&.Mui-active': { color: '#ff9900' },
                      '&.Mui-completed': { color: '#4caf50' },
                      fontSize: { xs: '1.2rem', sm: '1.5rem' },
                    },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{step.label}</Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 4 }, maxWidth: 900, mx: "auto", borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Box sx={{ textAlign: 'center', mb: 3, pb: 2, borderBottom: '2px solid #f0f0f0' }}>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#333', mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            {isEditMode ? "Agent Profile" : "Agent Registration"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            {isEditMode ? "Update your agent profile information" : "Complete all steps to create your agent account"}
          </Typography>
          <Box sx={{ mt: 2, display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.5, bgcolor: '#fff3e0', borderRadius: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Typography variant="body2" fontWeight={600} sx={{ color: '#e65100', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Step {activeStep + 1} of {STEPS.length}:
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {STEPS[activeStep]?.label}
            </Typography>
          </Box>
        </Box>

        <form>
          {activeStep === 0 && (
            <Fade in>
              <Box>
                {/* Profile Image Upload */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Box sx={{ display: 'inline-block', position: 'relative' }}>
                    <Avatar
                      src={profilePic}
                      sx={{
                        width: { xs: 80, sm: 100 },
                        height: { xs: 80, sm: 100 },
                        fontSize: { xs: 32, sm: 40 },
                        fontWeight: 700,
                        bgcolor: getAvatarColor(watch("name")),
                        border: '3px solid #ff9900',
                        boxShadow: '0 4px 16px rgba(255,153,0,0.2)',
                      }}
                    >
                      {getInitials(watch("name"))}
                    </Avatar>
                    <IconButton
                      size="small"
                      onClick={() => profileInputRef.current?.click()}
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        bgcolor: '#ff9900',
                        color: '#fff',
                        '&:hover': { bgcolor: '#e68a00' },
                        boxShadow: '0 2px 8px rgba(255,153,0,0.4)',
                      }}
                    >
                      <Upload fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                    Click to upload profile photo
                  </Typography>
                  <input
                    type="file"
                    hidden
                    ref={profileInputRef}
                    accept="image/*"
                    onChange={handleProfileUpload}
                  />
                </Box>

                {/* Change Password Button — edit mode only, suppress during new registration */}
                {isEditMode && !needsPassword && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                      startIcon={<Lock />}
                      onClick={() => setOpenPasswordDialog(true)}
                      variant="text"
                      size="small"
                      sx={{ color: '#666' }}
                    >
                      Change Password
                    </Button>
                  </Box>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  {renderModernField("Full Name *", renderModernTextField({
                    ...register("name"),
                    error: !!errors.name,
                    helperText: errors.name?.message,
                    InputLabelProps: { shrink: true },
                    placeholder: "Enter your full name",
                  }))}
                  {renderModernField("Email Address *", renderModernTextField({
                    ...register("email", { setValueAs: (v) => v?.toLowerCase?.() ?? v }),
                    error: !!errors.email,
                    helperText: errors.email?.message,
                    InputLabelProps: { shrink: true },
                    placeholder: "your@email.com",
                    type: "email",
                    inputProps: { style: { textTransform: 'lowercase' } },
                  }))}
                </Box>

                <Box sx={{ mt: 2 }}>
                  {renderModernField("Phone Number *", (
                    <Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                          {renderModernTextField({
                            ...register("mobile"),
                            error: !!errors.mobile,
                            helperText: errors.mobile?.message,
                            placeholder: "Enter phone number",
                          })}
                        </Box>
                        {!otp.phoneVerified && (
                          <Button
                            variant="contained"
                            onClick={handleSendOTP}
                            disabled={otp.otpLoading || otp.otpTimer > 0}
                            sx={{ ...modernButtonStyle, bgcolor: otp.otpTimer > 0 ? '#ccc' : '#ff9900', whiteSpace: 'nowrap', width: { xs: '100%', sm: 'auto' } }}
                          >
                            {otp.otpTimer > 0 ? `${otp.otpTimer}s` : "Send OTP"}
                          </Button>
                        )}
                        {otp.phoneVerified && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 1, bgcolor: '#e8f5e9', borderRadius: 2, width: { xs: '100%', sm: 'auto' }, justifyContent: 'center' }}>
                            <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>Verified</Typography>
                          </Box>
                        )}
                      </Box>

                      {otp.otpSent && !otp.phoneVerified && (
                        <Fade in>
                          <Box sx={{ mt: 2, p: 2, bgcolor: '#fff8e1', borderRadius: 2, border: '1px solid #ffecb3' }}>
                            <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#f57c00' }}>
                              OTP sent to your WhatsApp
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                              <TextField
                                value={otp.otpCode}
                                onChange={(e) => otp.setOtpCode(e.target.value)}
                                placeholder="Enter 4-digit OTP"
                                size="small"
                                sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}
                                inputProps={{ maxLength: 4 }}
                              />
                              <Button
                                variant="contained"
                                onClick={handleVerifyOTP}
                                sx={{ ...modernButtonStyle, bgcolor: '#ff9900', width: { xs: '100%', sm: 'auto' } }}
                              >
                                Verify
                              </Button>
                            </Box>
                          </Box>
                        </Fade>
                      )}
                    </Box>
                  ))}
                </Box>

                {needsPassword && (
                  <Box sx={{ mt: 3, p: 3, bgcolor: '#f8f9fa', borderRadius: 3, border: '1px solid #e0e0e0' }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: '#333' }}>
                      🔐 Set Your Password
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                      {renderModernField("Password", renderModernTextField({
                        type: showPassword ? "text" : "password",
                        ...register("password"),
                        error: !!errors.password,
                        helperText: errors.password?.message,
                        placeholder: "Create a strong password",
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }))}
                      {renderModernField("Confirm Password", renderModernTextField({
                        type: showConfirmPassword ? "text" : "password",
                        ...register("confirmPassword"),
                        error: !!errors.confirmPassword,
                        helperText: errors.confirmPassword?.message,
                        placeholder: "Confirm your password",
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} size="small">
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }))}
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(getPasswordStrength(password) / 4) * 100}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: strengthColor[getPasswordStrength(password)],
                            borderRadius: 4,
                          },
                        }}
                      />
                      <Typography variant="caption" sx={{ mt: 0.5, color: '#666' }}>
                        Password strength: {strengthLabel[getPasswordStrength(password)]}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Fade>
          )}

          {activeStep === 1 && (
            <Fade in>
              <Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Area of Operation</RequiredLabel>
                  </Typography>
                  {renderModernTextField({
                    select: true,
                    value: watch("areaOfOperation") || "",
                    ...register("areaOfOperation"),
                    error: !!errors.areaOfOperation,
                    children: [
                      <MenuItem key="India" value="India">India</MenuItem>,
                      <MenuItem key="China" value="China">China</MenuItem>,
                      <MenuItem key="Both" value="Both">Both (India & China)</MenuItem>,
                    ],
                  })}
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Business Type</RequiredLabel>
                  </Typography>
                  {renderModernTextField({
                    select: true,
                    value: watch("agentType") || "",
                    ...register("agentType"),
                    error: !!errors.agentType,
                    children: [
                      <MenuItem key="Individual" value="Individual">Individual</MenuItem>,
                      <MenuItem key="Private Limited" value="Private Limited">Private Limited</MenuItem>,
                      <MenuItem key="Partnership" value="Partnership">Partnership</MenuItem>,
                      <MenuItem key="Sole Proprietorship" value="Sole Proprietorship">Sole Proprietorship</MenuItem>,
                      <MenuItem key="LLP" value="LLP">LLP</MenuItem>,
                      <MenuItem key="Corporation" value="Corporation">Corporation</MenuItem>,
                    ],
                  })}
                </Box>
                
                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Preferred Language</RequiredLabel>
                  </Typography>
                  {renderModernTextField({
                    select: true,
                    value: watch("preferredLanguage") || "",
                    ...register("preferredLanguage"),
                    error: !!errors.preferredLanguage,
                    children: [
                      <MenuItem key="English" value="English">English</MenuItem>,
                      <MenuItem key="Mandarin" value="Mandarin">Mandarin</MenuItem>,
                      <MenuItem key="Cantonese" value="Cantonese">Cantonese</MenuItem>,
                    ],
                  })}
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Legal Name</RequiredLabel>
                  </Typography>
                  {renderModernTextField({
                    ...register("legalName"),
                    error: !!errors.legalName,
                    helperText: errors.legalName?.message,
                  })}
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Business Reg Number</RequiredLabel>
                  </Typography>
                  {renderModernTextField({
                    ...register("businessRegNumber"),
                    error: !!errors.businessRegNumber,
                    helperText: errors.businessRegNumber?.message,
                  })}
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Incorporation Date</RequiredLabel>
                  </Typography>
                  {renderModernTextField({
                    type: "date",
                    ...register("incorporationDate"),
                    error: !!errors.incorporationDate,
                    helperText: errors.incorporationDate?.message,
                    InputLabelProps: { shrink: true },
                  })}
                </Box>
              </Box>

              {/* India Compliance Fields */}
              {(watch("areaOfOperation") === "India" || watch("areaOfOperation") === "Both") && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }}>
                    <Typography variant="caption" fontWeight={600} sx={{ color: '#1976d2', bgcolor: '#fff', border: '1.5px solid #1976d2', borderRadius: 1, px: 1.5, py: 0.25 }}>
                      India Compliance
                    </Typography>
                  </Divider>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                        GSTIN
                      </Typography>
                      {renderModernTextField({
                        placeholder: "22AAAAA0000A1Z5",
                        inputProps: { maxLength: 15 },
                        ...register("gstin"),
                        error: !!errors.gstin,
                        helperText: errors.gstin?.message || "At least one of GSTIN / PAN / IEC required",
                      })}
                    </Box>

                    <Box>
                      <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                        PAN Number
                      </Typography>
                      {renderModernTextField({
                        placeholder: "AAAAA0000A",
                        inputProps: { maxLength: 10 },
                        ...register("panNumber"),
                        error: !!errors.panNumber,
                        helperText: errors.panNumber?.message,
                      })}
                    </Box>

                    <Box>
                      <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                        IEC Code
                      </Typography>
                      {renderModernTextField({
                        placeholder: "AACPA5134M",
                        inputProps: { maxLength: 10 },
                        ...register("iecCode"),
                        error: !!errors.iecCode,
                        helperText: errors.iecCode?.message,
                      })}
                    </Box>

                    <DocUpload
                      label="GST Certificate (Optional)"
                      accentColor="#1976d2"
                      file={gstCertFile}
                      preview={gstCertPreview}
                      onFileChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        setGstCertFile(f);
                        setGstCertPreview(f && f.type.startsWith('image/') ? URL.createObjectURL(f) : '');
                        setDeletedDocs(d => d.filter(x => x !== 'gstCertificate'));
                      }}
                      onDelete={() => { setGstCertFile(null); setGstCertPreview(''); setDeletedDocs(d => [...d, 'gstCertificate']); }}
                    />

                    <DocUpload
                      label="PAN Card (Optional)"
                      accentColor="#1976d2"
                      file={panCardFile}
                      preview={panCardPreview}
                      onFileChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        setPanCardFile(f);
                        setPanCardPreview(f && f.type.startsWith('image/') ? URL.createObjectURL(f) : '');
                        setDeletedDocs(d => d.filter(x => x !== 'panCard'));
                      }}
                      onDelete={() => { setPanCardFile(null); setPanCardPreview(''); setDeletedDocs(d => [...d, 'panCard']); }}
                    />

                    <DocUpload
                      label="IEC Certificate (Optional)"
                      accentColor="#1976d2"
                      file={iecCertFile}
                      preview={iecCertPreview}
                      onFileChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        setIecCertFile(f);
                        setIecCertPreview(f && f.type.startsWith('image/') ? URL.createObjectURL(f) : '');
                        setDeletedDocs(d => d.filter(x => x !== 'iecCertificate'));
                      }}
                      onDelete={() => { setIecCertFile(null); setIecCertPreview(''); setDeletedDocs(d => [...d, 'iecCertificate']); }}
                    />
                  </Box>
                </Box>
              )}

              {/* China Compliance Fields */}
              {(watch("areaOfOperation") === "China" || watch("areaOfOperation") === "Both") && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }}>
                    <Typography variant="caption" fontWeight={600} sx={{ color: '#d32f2f', bgcolor: '#fff', border: '1.5px solid #d32f2f', borderRadius: 1, px: 1.5, py: 0.25 }}>
                      China Compliance
                    </Typography>
                  </Divider>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                        <RequiredLabel>USCC Code</RequiredLabel>
                      </Typography>
                      {renderModernTextField({
                        placeholder: "18-character unified social credit code",
                        inputProps: { maxLength: 18 },
                        ...register("usccCode"),
                        error: !!errors.usccCode,
                        helperText: errors.usccCode?.message || "18-character alphanumeric code",
                      })}
                    </Box>

                    <Box>
                      <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                        China Business License Number
                      </Typography>
                      {renderModernTextField({
                        ...register("chinaBusinessLicenseNumber"),
                        error: !!errors.chinaBusinessLicenseNumber,
                        helperText: errors.chinaBusinessLicenseNumber?.message,
                      })}
                    </Box>

                    <DocUpload
                      label="USCC Certificate (Optional)"
                      accentColor="#d32f2f"
                      file={usccCertFile}
                      preview={usccCertPreview}
                      onFileChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        setUsccCertFile(f);
                        setUsccCertPreview(f && f.type.startsWith('image/') ? URL.createObjectURL(f) : '');
                        setDeletedDocs(d => d.filter(x => x !== 'usccCertificate'));
                      }}
                      onDelete={() => { setUsccCertFile(null); setUsccCertPreview(''); setDeletedDocs(d => [...d, 'usccCertificate']); }}
                    />

                    <DocUpload
                      label="China Business License (Optional)"
                      accentColor="#d32f2f"
                      file={chinaLicenseFile}
                      preview={chinaLicensePreview}
                      onFileChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        setChinaLicenseFile(f);
                        setChinaLicensePreview(f && f.type.startsWith('image/') ? URL.createObjectURL(f) : '');
                        setDeletedDocs(d => d.filter(x => x !== 'chinaBusinessLicense'));
                      }}
                      onDelete={() => { setChinaLicenseFile(null); setChinaLicensePreview(''); setDeletedDocs(d => [...d, 'chinaBusinessLicense']); }}
                    />
                  </Box>
                </Box>
              )}
              </Box>
            </Fade>
          )}
          {activeStep === 2 && (
            <Fade in>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Contact Person</RequiredLabel>
                  </Typography>
                  {renderModernTextField({
                    ...register("contactPerson"),
                    error: !!errors.contactPerson,
                    helperText: errors.contactPerson?.message,
                  })}
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Designation</RequiredLabel>
                  </Typography>
                  {renderModernTextField({
                    ...register("designation"),
                    error: !!errors.designation,
                    helperText: errors.designation?.message,
                  })}
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Contact Phone</RequiredLabel>
                  </Typography>
                  {renderModernTextField({
                    ...register("contactPhone"),
                    error: !!errors.contactPhone,
                    helperText: errors.contactPhone?.message,
                  })}
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    Contact Email
                  </Typography>
                  {renderModernTextField({
                    type: "email",
                    ...register("contactEmail"),
                    error: !!errors.contactEmail,
                    helperText: errors.contactEmail?.message,
                  })}
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    Alternative Contact
                  </Typography>
                  {renderModernTextField({
                    ...register("altContact"),
                  })}
                </Box>

                <Box>
                  <DocUpload
                    label="Business License (Optional)"
                    file={businessLicense}
                    preview={businessLicensePreview}
                    onFileChange={handleFileChange}
                    onDelete={() => {
                      setBusinessLicense(null);
                      setBusinessLicensePreview("");
                      setBusinessLicenseDeleted(true);
                    }}
                  />
                </Box>
              </Box>
            </Fade>
          )}

          {activeStep === 3 && (
            <Fade in>
              <Box>
                {/* Map Picker - Full Width */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1.5, color: '#555' }}>
                    📍 Business Location
                  </Typography>
                  <MapPicker
                    setValue={(name, value) => setValue(`address.${name}`, value)}
                    initialLocation={initialLocation}
                  />
                </Box>

                {/* Street Address */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                    <RequiredLabel>Street Address</RequiredLabel>
                  </Typography>
                  {renderModernTextField({
                    ...register("address.addressLine1"),
                    error: !!errors.address?.addressLine1,
                    helperText: errors.address?.addressLine1?.message || "Enter your business street address",
                    InputLabelProps: { shrink: true },
                    placeholder: "Enter your business address",
                  })}
                </Box>

                {/* City & State Row */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                      <RequiredLabel>City</RequiredLabel>
                    </Typography>
                    {renderModernTextField({
                      ...register("address.city"),
                      error: !!errors.address?.city,
                      helperText: errors.address?.city?.message,
                      InputLabelProps: { shrink: true },
                      placeholder: "City",
                    })}
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                      <RequiredLabel>State</RequiredLabel>
                    </Typography>
                    {renderModernTextField({
                      ...register("address.state"),
                      error: !!errors.address?.state,
                      helperText: errors.address?.state?.message,
                      InputLabelProps: { shrink: true },
                      placeholder: "State",
                    })}
                  </Box>
                </Box>

                {/* Pincode & Website Row */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                      <RequiredLabel>Pincode</RequiredLabel>
                    </Typography>
                    {renderModernTextField({
                      ...register("address.pincode"),
                      error: !!errors.address?.pincode,
                      helperText: errors.address?.pincode?.message,
                      InputLabelProps: { shrink: true },
                      placeholder: "Postal code",
                    })}
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: '#555' }}>
                      Website (Optional)
                    </Typography>
                    {renderModernTextField({
                      ...register("website"),
                      InputLabelProps: { shrink: true },
                    })}
                  </Box>
                </Box>

                {/* Hidden lat/lng fields for form submission */}
                {/* <input type="hidden" {...register("latitude")} />
                <input type="hidden" {...register("longitude")} /> */}
              </Box>
            </Fade>
          )}

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            justifyContent: 'space-between',
            mt: 3,
            pt: 2,
            borderTop: '2px solid #f0f0f0',
            gap: { xs: 2, sm: 0 }
          }}>
            <Button
              type="button"
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<ArrowBack />}
              sx={{
                borderRadius: 2,
                px: 3,
                borderColor: '#ddd',
                color: '#666',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': { borderColor: '#ff9900', color: '#ff9900' },
              }}
            >
              Back
            </Button>

            {activeStep >= STEPS.length - 1 ? (
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                onClick={(e) => { e.preventDefault(); handleFinalSubmit(); }}
                sx={{ ...modernButtonStyle, bgcolor: '#4caf50', px: 4, width: { xs: '100%', sm: 'auto' } }}
              >
                {loading ? "Saving..." : "Save Changes ✓"}
              </Button>
            ) : (
              <Button
                type="button"
                variant="contained"
                onClick={handleNext}
                disabled={activeStep >= STEPS.length - 1}
                endIcon={<ArrowForward />}
                sx={{ ...modernButtonStyle, bgcolor: activeStep >= STEPS.length - 1 ? '#ccc' : '#ff9900' }}
              >
                Next
              </Button>
            )}
          </Box>
        </form>

        <Typography variant="body2" sx={{ mt: 3, textAlign: "center", color: "#666" }}>
          By registering, you agree to Project X's Terms of Service and Privacy Policy.
        </Typography>
      </Paper>

      {/* Change Password Dialog */}
      <Dialog
        open={openPasswordDialog}
        onClose={() => setOpenPasswordDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
            <Lock sx={{ color: '#ff9900' }} />
            <Typography variant="h6" fontWeight={600}>Change Password</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Current Password"
            type={showCurrent ? "text" : "password"}
            margin="normal"
            variant="outlined"
            {...registerPassword("currentPassword")}
            error={!!passwordErrors.currentPassword}
            helperText={passwordErrors.currentPassword?.message}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowCurrent(!showCurrent)}>
                    {showCurrent ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="New Password"
            type={showNew ? "text" : "password"}
            margin="normal"
            variant="outlined"
            {...registerPassword("newPassword")}
            error={!!passwordErrors.newPassword}
            helperText={passwordErrors.newPassword?.message}
            onChange={(e) => {
              registerPassword("newPassword").onChange(e);
              setPasswordStrength(evaluatePasswordStrength(e.target.value));
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNew(!showNew)}>
                    {showNew ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <LinearProgress
            variant="determinate"
            value={(passwordStrength.score / 4) * 100}
            sx={{
              height: 8,
              borderRadius: 4,
              my: 1,
              bgcolor: "#eee",
              "& .MuiLinearProgress-bar": {
                bgcolor: passwordStrength.color,
                borderRadius: 4,
              },
            }}
          />

          <TextField
            fullWidth
            label="Confirm New Password"
            type={showConfirmNew ? "text" : "password"}
            margin="normal"
            variant="outlined"
            {...registerPassword("confirmNewPassword")}
            error={!!passwordErrors.confirmNewPassword}
            helperText={passwordErrors.confirmNewPassword?.message}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirmNew(!showConfirmNew)}>
                    {showConfirmNew ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenPasswordDialog(false)}
            sx={{ borderRadius: 2, color: '#666' }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            onClick={handlePasswordSubmit(handlePasswordChange)}
            sx={{ ...modernButtonStyle, bgcolor: '#ff9900', borderRadius: 2 }}
          >
            Update Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentRegistration;